/**
 * Pure helpers for the user store, including QA impersonation (ND-2067). Kept
 * free of the store and API imports so the unit specs can load them.
 */

/**
 * The GET /v2/newsdesk/access payload in state.access shape. The impersonation
 * endpoints return the same payload, so both paths map it here.
 */
export function toAccess(raw = {}) {
  const access = {
    admin: raw.admin ?? false,
    dashboard: raw.dashboard ?? false,
    user: raw.user ?? [],
    viewer: raw.viewer ?? [],
    stagehand: raw.stagehand ?? [],
    workspaceGroups: raw.workspace_groups ?? [],
    oktaGroups: raw.okta_groups ?? []
  }
  access.stations = [...new Set([...access.user, ...access.viewer, ...access.stagehand])]
  return access
}

// Profile fields that identify the user. While impersonating, the JWT is still
// the admin's, so these come from the target instead.
export const IDENTITY_FIELDS = ['empid', 'user_id', 'first_name', 'last_name', 'email_address']

/** The profile with its identity fields taken from source, when source has them. */
export function withIdentityOf(profile, source) {
  if (!source) return profile
  const identity = Object.fromEntries(IDENTITY_FIELDS.filter((field) => source[field] !== undefined).map((field) => [field, source[field]]))
  return { ...profile, ...identity }
}

/**
 * How the session this browser believes in compares with the API's
 * GET /v2/auth/impersonate/active response. A session is identified by its
 * start time, so restarting on the same target elsewhere also counts as a change.
 *
 * @returns {'unchanged'|'ended'|'changed'}
 */
export function impersonationStatus(believedSession, active) {
  const believed = believedSession?.started_at ?? null
  const actual = active?.active ? (active.session?.started_at ?? null) : null
  if (believed === actual) return 'unchanged'
  if (!actual) return 'ended'
  return 'changed'
}

/** The impersonated user's display name for a { target_user } session, or ''. */
export function impersonatedName(impersonation) {
  const target = impersonation?.target_user
  if (!target) return ''
  return `${target.first_name ?? ''} ${target.last_name ?? ''}`.trim() || `empid ${target.empid}`
}

/**
 * Whether this tab should ask the API about impersonation. It is decided from
 * what this tab loaded with, never from ews_user: every tab shares that, so
 * another tab's start or exit lands there before this tab has reloaded.
 * Admins check so a session started elsewhere is noticed; a tab loaded while
 * impersonating checks so one that ended elsewhere is. After the API has
 * refused (403 in production) there is nothing to check.
 */
export function shouldCheckImpersonation({ refused = false, loadedImpersonation = null, loadedAsAdmin = false } = {}) {
  if (refused) return false
  return !!loadedImpersonation || !!loadedAsAdmin
}
